import * as yup from 'yup'
import { ApplicationFormAnswers, PersonalDetailsType } from "../components/ApplicationForm/ApplicationFormContext"
import { AttributionDetails } from "../components/ApplicationForm/types"
import DrivingLicenceValidator from "../validators/DrivingLicenceValidator"
import EmailAddressValidator from "../validators/EmailAddressValidator"
import LoanAmountValidator from "../validators/LoanAmountValidator"
import MaritalStatusValidator from "../validators/MaritalStatusValidator"
import PersonalDetailsValidator from "../validators/PersonalDetailsValidator"
import TitleValidator from "../validators/TitleValidator"
import VehicleTypeValidator from "../validators/VehicleTypeValidator"
import { isContactNumberValid } from "../validators/CustomerDetailsValidator"


const NO_LOAN_AMOUNT_SPECIFIED = 'Not sure yet'

const isFieldValid = (schema: yup.AnySchema, field: string, value: unknown): boolean => {
  const fieldSchema = (schema as unknown as { fields: Record<string, yup.AnySchema> }).fields[field]
  return fieldSchema.isValidSync(value)
}

// Returns a shallow copy of prefillData with any individually-invalid field blanked out (set to
// Will show a 'Required' prompt for just the bad field instead of rendering incorrect prefilled data
export const getSanitisedPrefillData = (
  prefillData: ApplicationFormAnswers,
  source?: AttributionDetails['source']
): ApplicationFormAnswers => {
  const personalDetails: PersonalDetailsType = {
    ...prefillData.personalDetails,
    dateOfBirth: prefillData.personalDetails?.dateOfBirth ?? prefillData.dateOfBirth
  }
  const answers: ApplicationFormAnswers = { ...prefillData, personalDetails }
  const isDrivingLicenceValid = DrivingLicenceValidator().validateState(answers)

  const isLoanAmountValid = !answers.loanAmount ||
    answers.loanAmount === NO_LOAN_AMOUNT_SPECIFIED ||
    LoanAmountValidator({ source }).validateState(answers)

  const hasValidContactNumber = isContactNumberValid(personalDetails.contactNumber)

  const personalDetailsSchema = PersonalDetailsValidator({ source }).getValidator()
  const dob = personalDetails.dateOfBirth
  const isFirstNameValid = isFieldValid(personalDetailsSchema, 'firstName', personalDetails.firstName?.trim())
  const isSurnameValid = isFieldValid(personalDetailsSchema, 'surname', personalDetails.surname?.trim())
  const isDateOfBirthValid = isFieldValid(
    personalDetailsSchema,
    'dateOfBirth',
    dob ? `${dob.day}-${dob.month}-${dob.year}` : ''
  )
  const isTitleValid = isFieldValid(TitleValidator().getValidator(), 'title', personalDetails.title?.toLowerCase())
  const isEmailAddressValid = isFieldValid(EmailAddressValidator().getValidator(), 'emailAddress', personalDetails.emailAddress)
  const isVehicleTypeValid = isFieldValid(VehicleTypeValidator().getValidator(), 'vehicleType', answers.vehicleType)
  const isMaritalStatusValid = isFieldValid(MaritalStatusValidator().getValidator(), 'maritalStatus', answers.maritalStatus?.toLowerCase())

  return {
    ...prefillData,
    personalDetails: prefillData.personalDetails && {
      ...prefillData.personalDetails,
      firstName: isFirstNameValid ? prefillData.personalDetails.firstName : undefined,
      surname: isSurnameValid ? prefillData.personalDetails.surname : undefined,
      title: isTitleValid ? prefillData.personalDetails.title : undefined,
      emailAddress: isEmailAddressValid ? prefillData.personalDetails.emailAddress : undefined,
      contactNumber: hasValidContactNumber ? prefillData.personalDetails.contactNumber : undefined,
      dateOfBirth: isDateOfBirthValid ? prefillData.personalDetails.dateOfBirth : undefined
    },
    maritalStatus: isMaritalStatusValid ? prefillData.maritalStatus : undefined,
    dateOfBirth: isDateOfBirthValid ? prefillData.dateOfBirth : undefined,
    vehicleType: isVehicleTypeValid ? prefillData.vehicleType : undefined,
    drivingLicence: isDrivingLicenceValid ? prefillData.drivingLicence : undefined,
    loanAmount: isLoanAmountValid ? prefillData.loanAmount : undefined
  }
}